import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { queryUsersKey, queryDoctorSecretariesKey, queryPatientHealthHistoryKey } from "../apis/queryKeys";
import { useUserByIdQuery } from "../apis/api.users";
import Spinner from "./common/Spinner";
import Button from "./common/Button";
import config from "../config";
import { useData } from "../lib/DataContext";
import { formatDateDMY, formatDateTimeDMY } from "../lib/ScheduleUtils";
import {
  useUpdateUserMutation,
  fetchDoctorSecretaries,
  createDoctorSecretary,
  removeDoctorSecretary,
  createSubscription,
  getUserSubscription,
} from "../apis/api.users";
import * as Tabs from "@radix-ui/react-tabs";
import {
  Trash2Icon,
  Info,
  Eye,
  EyeOff,
  User,
  Phone,
  MapPin,
  Heart,
  Shield,
  Calendar,
  Droplets,
  AlertCircle,
  Contact,
  Clock,
  Stethoscope,
  Camera,
  Languages,
  ImageIcon,
  Building2,
  Landmark,
  ArrowLeft,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./common/Card";
import { Input } from "./common/Input";
import { Label } from "./common/Label";
import { Textarea } from "./common/Textarea";
import { useAuth } from "../lib/AuthContext";
import {
  Tooltip,
  TooltipTrigger,
  TooltipArrow,
  TooltipContent,
} from "./common/Tooltip";
import customToast from "./common/Toaster";
import PatientStudiesTab from "./PatientStudiesTab";
import DependentsTab from "./DependentsTab";
import MonedasTab from "./inventory/MonedasTab";
import PatientHealthHistoryDialog from "./PatientHealthHistoryDialog";
import { HealthInsuranceSelect, HealthInsuranceMultiSelect } from "./HealthInsurance";
import { canActAsPatient } from "../utils/utils";
import useBrandTranslation from "../hooks/useBrandTranslation";
import LanguageSwitcher from "./common/LanguageSwitcher";
import {
  ALLOWED_PATIENT_BOOKING_HORIZON_DAYS,
  normalizePatientBookingHorizonDays,
} from "../lib/bookingHorizon";

const DISABILITY_OPTIONS = (t) => [
  { value: "none", label: t("app.profile.disabilityNone", "Ninguna") },
  { value: "sensory", label: t("app.profile.disabilitySensory", "Sensorial (visual y auditiva)") },
  { value: "physical", label: t("app.profile.disabilityPhysical", "Física") },
  { value: "mental", label: t("app.profile.disabilityMental", "Mental") },
  { value: "intellectual", label: t("app.profile.disabilityIntellectual", "Intelectual") },
];

const LANGUAGE_OPTIONS = [
  { code: "es", label: "🇦🇷 Español" },
  { code: "en", label: "🇺🇸 Inglés" },
  { code: "pt", label: "🇧🇷 Portugués" },
  { code: "fr", label: "🇫🇷 Francés" },
  { code: "de", label: "🇩🇪 Alemán" },
  { code: "it", label: "🇮🇹 Italiano" },
  { code: "zh", label: "🇨🇳 Chino" },
  { code: "ja", label: "🇯🇵 Japonés" },
  { code: "ko", label: "🇰🇷 Coreano" },
  { code: "ar", label: "🇸🇦 Árabe" },
  { code: "he", label: "🇮🇱 Hebreo" },
  { code: "ru", label: "🇷🇺 Ruso" },
];

const PROFESSIONAL_TITLES = (t) => [
  { value: "", label: t("app.profile.select", "Seleccionar") },
  { value: "Dr.", label: "Dr." },
  { value: "Dra.", label: "Dra." },
];

const INSTITUTION_TYPES = (t) => [
  { value: "", label: t("app.profile.select", "Seleccionar") },
  { value: "consultorio", label: t("app.profile.instConsultorio", "Consultorio") },
  { value: "poli_consultorio", label: t("app.profile.instPoli", "Poli Consultorio") },
  { value: "clinica", label: t("app.profile.instClinica", "Clínica") },
  { value: "sanatorio", label: t("app.profile.instSanatorio", "Sanatorio") },
  { value: "otro", label: t("app.profile.instOtro", "Otro") },
];

function calcAge(birthDate) {
  if (!birthDate) return "";
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// Campos del perfil de paciente. `hideIdentityFields` los reusa en el perfil de un
// profesional que se atiende como paciente: DNI, teléfono y las fechas de registro
// ya se editan/muestran en su tab "Información", así que no se repiten acá.
function PatientProfileFields({
  register,
  errors,
  user,
  watch,
  setValue,
  DISABILITY_OPTIONS,
  calcAge,
  hideIdentityFields = false,
}) {
  const { t } = useBrandTranslation();
  const birthDate = watch("birth_date");
  const disability = watch("disability");
  const hasDisability = disability && disability !== "none";
  const healthInsuranceName = watch("health_insurance_name");

  const inputClass =
    "w-full rounded border border-slate-200 p-2.5 text-slate-700 text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="space-y-6 border-t border-gray-200 pt-6">
      {/* Datos personales */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-5 w-5 text-slate-500" />
            {t("app.profile.personalData", "Datos personales")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {!hideIdentityFields && (
              <div>
                <Label htmlFor="dni" className={labelClass}>DNI</Label>
                <Input
                  id="dni"
                  {...register("dni")}
                  className={inputClass}
                  placeholder={t("app.profile.phDni", "Ej: 12345678")}
                />
              </div>
            )}
            <div>
              <Label htmlFor="birth_date" className={labelClass}>{t("app.profile.birthDate", "Fecha de nacimiento")}</Label>
              <Input
                id="birth_date"
                type="date"
                {...register("birth_date")}
                className={inputClass}
              />
            </div>
            <div>
              <Label htmlFor="gender" className={labelClass}>{t("app.profile.gender", "Género")}</Label>
              <select
                id="gender"
                {...register("gender")}
                className={inputClass}
              >
                <option value="">{t("app.profile.select", "Seleccionar")}</option>
                <option value="male">{t("app.profile.genderMale", "Masculino")}</option>
                <option value="female">{t("app.profile.genderFemale", "Femenino")}</option>
                <option value="other">{t("app.profile.genderOther", "Otro")}</option>
                <option value="prefer_not">{t("app.profile.genderPreferNot", "Prefiero no decir")}</option>
              </select>
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.age", "Edad")}</Label>
              <div className="rounded border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-600">
                {birthDate ? `${calcAge(birthDate)} ${t("app.profile.years", "años")}` : "—"}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacto */}
      {!hideIdentityFields && (
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Phone className="h-5 w-5 text-slate-500" />
              {t("app.profile.contact", "Contacto")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div>
              <Label className={labelClass}>{t("app.profile.phone", "Teléfono")}</Label>
              <Input
                {...register("phone", { required: true })}
                className={inputClass}
                placeholder={t("app.profile.phPhone", "Ej: +54 11 1234-5678")}
              />
              {errors.phone && (
                <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
              )}
              <p className="mt-1.5 text-xs text-slate-500">
                {t("app.profile.phonePatientHint", "Se usa en tus turnos y para que el equipo médico te contacte. Podés incluir código de país.")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dirección */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-5 w-5 text-slate-500" />
            {t("app.profile.address", "Dirección")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.street", "Calle y número")}</Label>
            <Input
              {...register("address_street")}
              className={inputClass}
              placeholder={t("app.profile.phStreet", "Ej: Av. Corrientes 1234")}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.floor", "Piso")}</Label>
              <Input {...register("address_floor")} className={inputClass} placeholder={t("app.profile.phFloor", "Ej: 2")} />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.apt", "Depto")}</Label>
              <Input {...register("address_apt")} className={inputClass} placeholder={t("app.profile.phApt", "Ej: A")} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className={labelClass}>{t("app.profile.city", "Localidad")}</Label>
              <Input {...register("city")} className={inputClass} placeholder={t("app.profile.phCity", "Ej: CABA")} />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.province", "Provincia")}</Label>
              <Input {...register("province")} className={inputClass} placeholder={t("app.profile.phProvince", "Ej: Buenos Aires")} />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.postalCode", "Código Postal")}</Label>
              <Input {...register("postal_code")} className={inputClass} placeholder={t("app.profile.phPostal", "Ej: 1043")} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Obra Social */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-5 w-5 text-slate-500" />
            {t("app.profile.healthInsurance", "Obra Social / Prepaga")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Label htmlFor="health_insurance_name" className={labelClass}>{t("app.profile.healthInsurance", "Obra Social / Prepaga")}</Label>
              <HealthInsuranceSelect
                id="health_insurance_name"
                value={healthInsuranceName || ""}
                onChange={(name) =>
                  setValue("health_insurance_name", name, { shouldDirty: true })
                }
              />
              <input type="hidden" {...register("health_insurance_name")} />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.healthInsuranceNumber", "Número")}</Label>
              <Input
                {...register("health_insurance_number")}
                className={inputClass}
                placeholder={t("app.profile.phAffiliate", "Nº afiliado")}
              />
            </div>
          </div>
          <div>
            <Label className={labelClass}>{t("app.profile.plan", "Plan")}</Label>
            <Input
              {...register("health_insurance_plan")}
              className={inputClass}
              placeholder={t("app.profile.phPlan", "Ej: 210, 310")}
            />
          </div>
        </CardContent>
      </Card>

      {/* Datos de salud */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Droplets className="h-5 w-5 text-slate-500" />
            {t("app.profile.healthData", "Datos de salud")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.height", "Altura (cm)")}</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                {...register("height_cm")}
                className={inputClass}
                placeholder={t("app.profile.phHeight", "Ej: 170")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.weight", "Peso (kg)")}</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                {...register("weight_kg")}
                className={inputClass}
                placeholder={t("app.profile.phWeight", "Ej: 75")}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.bloodType", "Grupo sanguíneo")}</Label>
              <select {...register("blood_type")} className={inputClass}>
                <option value="">{t("app.profile.select", "Seleccionar")}</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="AB">AB</option>
                <option value="O">O</option>
              </select>
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.bloodFactor", "Factor")}</Label>
              <select {...register("blood_factor")} className={inputClass}>
                <option value="">{t("app.profile.select", "Seleccionar")}</option>
                <option value="+">Rh+</option>
                <option value="-">Rh-</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className={labelClass}>{t("app.profile.smoking", "Tabaquismo")}</Label>
              <select {...register("smoking")} className={inputClass}>
                <option value="">{t("app.profile.select", "Seleccionar")}</option>
                <option value="yes">{t("app.profile.yes", "Sí")}</option>
                <option value="no">{t("app.profile.no", "No")}</option>
              </select>
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.sedentary", "Sedentarismo")}</Label>
              <select {...register("sedentary")} className={inputClass}>
                <option value="">{t("app.profile.select", "Seleccionar")}</option>
                <option value="yes">{t("app.profile.yes", "Sí")}</option>
                <option value="no">{t("app.profile.no", "No")}</option>
              </select>
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.toxics", "Tóxicos")}</Label>
              <select {...register("toxics")} className={inputClass}>
                <option value="">{t("app.profile.select", "Seleccionar")}</option>
                <option value="yes">{t("app.profile.yes", "Sí")}</option>
                <option value="no">{t("app.profile.no", "No")}</option>
              </select>
            </div>
          </div>
          <div>
            <Label className={labelClass}>{t("app.profile.antecedents", "Antecedentes")}</Label>
            <Textarea
              {...register("medical_antecedents")}
              className={inputClass}
              rows={4}
              placeholder={t("app.profile.antecedentsPlaceholder", "Intervenciones quirúrgicas, medicación permanente u otro dato relevante para tu médico")}
            />
          </div>
        </CardContent>
      </Card>

      {/* Discapacidad */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertCircle className="h-5 w-5 text-slate-500" />
            {t("app.profile.disability", "Discapacidad")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.disabilityType", "Tipo")}</Label>
            <select {...register("disability")} className={inputClass}>
              {DISABILITY_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          {hasDisability && (
            <div>
              <Label className={labelClass}>{t("app.profile.disabilityDetail", "Por favor detalle aquí:")}</Label>
              <Textarea
                {...register("disability_detail")}
                className={inputClass}
                rows={3}
                placeholder={t("app.profile.disabilityDetailPlaceholder", "Describa su situación")}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contacto de emergencia */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Contact className="h-5 w-5 text-slate-500" />
            {t("app.profile.emergencyContact", "Contacto de emergencia")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.name", "Nombre")}</Label>
              <Input {...register("emergency_contact_name")} className={inputClass} />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.lastName", "Apellido")}</Label>
              <Input {...register("emergency_contact_last_name")} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.phone", "Teléfono")}</Label>
              <Input
                {...register("emergency_contact_phone")}
                className={inputClass}
                placeholder={t("app.profile.phPhone", "Ej: +54 11 1234-5678")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.emergencyOther", "Otro")}</Label>
              <Input
                {...register("emergency_contact_other")}
                className={inputClass}
                placeholder={t("app.profile.emergencyOtherPlaceholder", "Otro medio de contacto")}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fechas (solo lectura) */}
      {!hideIdentityFields && (
        <Card className="overflow-hidden border-slate-200 bg-slate-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-slate-600">
              <Clock className="h-5 w-5 text-slate-500" />
              {t("app.profile.registration", "Registro")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0 text-sm text-slate-600">
            {user.created_at && (
              <p>
                <span className="font-medium">{t("app.profile.createdAt", "Fecha de creación:")}</span>{" "}
                {formatDateTimeDMY(user.created_at)}
              </p>
            )}
            {user.updated_at && (
              <p>
                <span className="font-medium">{t("app.profile.updatedAt", "Última modificación:")}</span>{" "}
                {formatDateTimeDMY(user.updated_at)}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Coberturas (obras sociales / prepagas) que atiende un profesional o institución.
// Se cruzan con la cobertura del paciente en la búsqueda y al reservar turno.
function AcceptedInsurancesCard({ register, watch, setValue }) {
  const { t } = useBrandTranslation();
  const accepted = watch("accepted_health_insurances");
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="h-5 w-5 text-slate-500" />
          {t("app.insurance.acceptedTitle", "Obras sociales y prepagas que atendés")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <p className="text-sm text-slate-500">
          {t(
            "app.insurance.acceptedHint",
            "Los pacientes verán en la búsqueda si atendés su cobertura. Si no la atendés, solo podrán reservar tus turnos particulares."
          )}
        </p>
        <HealthInsuranceMultiSelect
          value={accepted}
          onChange={(list) =>
            setValue("accepted_health_insurances", list, { shouldDirty: true })
          }
        />
        <input type="hidden" {...register("accepted_health_insurances")} />
      </CardContent>
    </Card>
  );
}

// Dirección del consultorio del profesional (distinta de su domicilio como
// paciente, que se carga en "Mi salud"). Se muestra y usa para la distancia en
// la búsqueda de profesionales.
function OfficeAddressCard({ register }) {
  const { t } = useBrandTranslation();
  const inputClass =
    "w-full rounded border border-slate-200 p-2.5 text-slate-700 text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-5 w-5 text-slate-500" />
          {t("app.profile.officeAddress", "Dirección del consultorio")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <p className="text-sm text-slate-500">
          {t(
            "app.profile.officeAddressHint",
            "Se muestra a los pacientes en la búsqueda junto con la distancia desde su domicilio."
          )}
        </p>
        <div>
          <Label className={labelClass}>{t("app.profile.street", "Calle y número")}</Label>
          <Input
            {...register("office_address_street")}
            className={inputClass}
            placeholder={t("app.profile.phStreet", "Ej: Av. Corrientes 1234")}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className={labelClass}>{t("app.profile.floor", "Piso")}</Label>
            <Input {...register("office_address_floor")} className={inputClass} placeholder={t("app.profile.phFloor", "Ej: 2")} />
          </div>
          <div>
            <Label className={labelClass}>{t("app.profile.apt", "Depto")}</Label>
            <Input {...register("office_address_apt")} className={inputClass} placeholder={t("app.profile.phApt", "Ej: A")} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <Label className={labelClass}>{t("app.profile.city", "Localidad")}</Label>
            <Input {...register("office_city")} className={inputClass} placeholder={t("app.profile.phCity", "Ej: CABA")} />
          </div>
          <div>
            <Label className={labelClass}>{t("app.profile.province", "Provincia")}</Label>
            <Input {...register("office_province")} className={inputClass} placeholder={t("app.profile.phProvince", "Ej: Buenos Aires")} />
          </div>
          <div>
            <Label className={labelClass}>{t("app.profile.postalCode", "Código Postal")}</Label>
            <Input {...register("office_postal_code")} className={inputClass} placeholder={t("app.profile.phPostal", "Ej: 1043")} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DoctorProfileFields({
  register,
  errors,
  user,
  watch,
  setValue,
  LANGUAGE_OPTIONS,
  PROFESSIONAL_TITLES,
}) {
  const { t } = useBrandTranslation();
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);

  const dniFrontImage = watch("dni_front_image");
  const dniBackImage = watch("dni_back_image");
  const profilePicture = watch("picture");

  useEffect(() => {
    if (user?.languages && Array.isArray(user.languages)) {
      setSelectedLanguages(user.languages);
    }
  }, [user?.languages]);

  const inputClass =
    "w-full rounded border border-slate-200 p-2.5 text-slate-700 text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  const handleLanguageToggle = (langCode) => {
    const updated = selectedLanguages.includes(langCode)
      ? selectedLanguages.filter((l) => l !== langCode)
      : selectedLanguages.length < 4
      ? [...selectedLanguages, langCode]
      : selectedLanguages;
    setSelectedLanguages(updated);
    setValue("languages", updated);
  };

  const handleImageUpload = async (file, field, setUploading) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("key", config.imgbbApiKey);
      const response = await fetch("https://api.imgbb.com/1/upload", {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        setValue(field, result.data.url);
      }
    } catch (err) {
      console.error("Error uploading image:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 border-t border-gray-200 pt-6">
      {/* Contacto */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-5 w-5 text-slate-500" />
            {t("app.profile.contact", "Contacto")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.phone", "Teléfono")}</Label>
            <Input
              {...register("phone", { required: true })}
              className={inputClass}
              placeholder={t("app.profile.phPhone", "Ej: +54 11 1234-5678")}
            />
            {errors.phone && (
              <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
            )}
            <p className="mt-1.5 text-xs text-slate-500">
              {t("app.profile.phoneDoctorHint", "Este número se muestra en la búsqueda y al reservar turnos contigo.")}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Datos profesionales */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Stethoscope className="h-5 w-5 text-blue-500" />
            Datos profesionales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className={labelClass}>{t("app.profile.abbreviation", "Abreviatura")}</Label>
              <select {...register("professional_title")} className={inputClass} style={{ height: "48px", marginTop: "5px" }}>
                {PROFESSIONAL_TITLES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.specialty", "Especialidad")}</Label>
              <Input
                {...register("specialization")}
                className={inputClass}
                placeholder={t("app.profile.phSpecialty", "Ej: Cardiología")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.licenseNumber", "Número de Matrícula")}</Label>
              <Input
                {...register("license_number")}
                className={inputClass}
                placeholder={t("app.profile.phLicense", "Ej: MN 12345")}
              />
            </div>
          </div>
          <div>
            <Label className={labelClass}>DNI</Label>
            <Input
              {...register("dni")}
              className={inputClass}
              placeholder={t("app.profile.phDni", "Ej: 12345678")}
            />
          </div>
        </CardContent>
      </Card>

      <OfficeAddressCard register={register} />

      {/* Verificación de identidad */}
      <Card className="overflow-hidden border-amber-200 bg-amber-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera className="h-5 w-5 text-amber-600" />
            Verificación de identidad
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <p className="text-sm text-amber-800">
            {t("app.profile.identityHint", "Te pedimos esto para validar que realmente eres médico/a. Las imágenes son confidenciales.")}
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.dniFront", "Foto DNI (Frente)")}</Label>
              <div className="space-y-2">
                {dniFrontImage && (
                  <img
                    src={dniFrontImage}
                    alt="DNI Frente"
                    className="h-24 w-auto rounded border object-cover"
                  />
                )}
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded border-2 border-dashed border-slate-300 bg-white p-3 text-sm text-slate-600 hover:bg-slate-50">
                  <ImageIcon className="h-4 w-4" />
                  {uploadingFront ? "Subiendo..." : "Subir imagen"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingFront}
                    onChange={(e) =>
                      handleImageUpload(
                        e.target.files?.[0],
                        "dni_front_image",
                        setUploadingFront
                      )
                    }
                  />
                </label>
                <input type="hidden" {...register("dni_front_image")} />
              </div>
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.dniBack", "Foto DNI (Dorso)")}</Label>
              <div className="space-y-2">
                {dniBackImage && (
                  <img
                    src={dniBackImage}
                    alt="DNI Dorso"
                    className="h-24 w-auto rounded border object-cover"
                  />
                )}
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded border-2 border-dashed border-slate-300 bg-white p-3 text-sm text-slate-600 hover:bg-slate-50">
                  <ImageIcon className="h-4 w-4" />
                  {uploadingBack ? "Subiendo..." : "Subir imagen"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingBack}
                    onChange={(e) =>
                      handleImageUpload(
                        e.target.files?.[0],
                        "dni_back_image",
                        setUploadingBack
                      )
                    }
                  />
                </label>
                <input type="hidden" {...register("dni_back_image")} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <AcceptedInsurancesCard register={register} watch={watch} setValue={setValue} />

      {/* Idiomas */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Languages className="h-5 w-5 text-slate-500" />
            Idiomas de atención
            <span className="ml-auto text-xs font-normal text-slate-400">
              {t("app.profile.languagesMax", "(máx. 4)")}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
            {LANGUAGE_OPTIONS.map(({ code, label }) => {
              const isSelected = selectedLanguages.includes(code);
              const isDisabled =
                !isSelected && selectedLanguages.length >= 4;
              return (
                <button
                  key={code}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleLanguageToggle(code)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : isDisabled
                      ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                      : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <input type="hidden" {...register("languages")} />
        </CardContent>
      </Card>

      {/* Foto de perfil */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-5 w-5 text-slate-500" />
            Foto de perfil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="flex items-center gap-4">
            {profilePicture ? (
              <img
                src={profilePicture}
                alt="Perfil"
                className="h-20 w-20 rounded-full border-2 border-slate-200 object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-slate-50">
                <User className="h-8 w-8 text-slate-400" />
              </div>
            )}
            <div className="flex-1">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <Camera className="h-4 w-4" />
                {uploadingProfile ? "Subiendo..." : "Cambiar foto"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingProfile}
                  onChange={(e) =>
                    handleImageUpload(
                      e.target.files?.[0],
                      "picture",
                      setUploadingProfile
                    )
                  }
                />
              </label>
              <p className="mt-1 text-xs text-slate-500">
                {t("app.profile.profilePhotoHint", "Esta imagen se mostrará en tu perfil público.")}
              </p>
              <input type="hidden" {...register("picture")} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fechas (solo lectura) */}
      <Card className="overflow-hidden border-slate-200 bg-slate-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-slate-600">
            <Clock className="h-5 w-5 text-slate-500" />
            {t("app.profile.registration", "Registro")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-0 text-sm text-slate-600">
          {user.created_at && (
            <p>
              <span className="font-medium">{t("app.profile.createdAt", "Fecha de creación:")}</span>{" "}
              {formatDateDMY(user.created_at)}
            </p>
          )}
          {user.updated_at && (
            <p>
              <span className="font-medium">{t("app.profile.updatedAt", "Última modificación:")}</span>{" "}
              {formatDateDMY(user.updated_at)}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InstitutionProfileFields({
  register,
  errors,
  user,
  watch,
  setValue,
  INSTITUTION_TYPES,
}) {
  const { t } = useBrandTranslation();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoUrl = watch("logo");

  const inputClass =
    "w-full rounded border border-slate-200 p-2.5 text-slate-700 text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  const handleImageUpload = async (file, field, setUploading) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      customToast(t("app.profile.fileTooLarge", "El archivo no puede superar los 5 MB"), "danger");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("key", config.imgbbApiKey);
      const response = await fetch("https://api.imgbb.com/1/upload", {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        setValue(field, result.data.url);
      }
    } catch (err) {
      console.error("Error uploading image:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 border-t border-gray-200 pt-6">
      {/* Datos de la institución */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-5 w-5 text-blue-500" />
            Datos de la institución
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>
              Nombre Fantasía
              <span className="ml-1 text-xs text-slate-500">
                {t("app.profile.fantasyNameHint", "(visible para pacientes y profesionales)")}
              </span>
            </Label>
            <Input
              {...register("fantasy_name")}
              className={inputClass}
              placeholder={t("app.profile.phInstitution", "Ej: Centro Médico San Martín")}
            />
          </div>
          <div>
            <Label className={labelClass}>{t("app.profile.institutionType", "Tipo de institución")}</Label>
            <select {...register("institution_type")} className={inputClass}>
              {INSTITUTION_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <AcceptedInsurancesCard register={register} watch={watch} setValue={setValue} />

      {/* Logo */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-5 w-5 text-slate-500" />
            Logo de la institución
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <p className="text-sm text-slate-500">
            {t("app.profile.logoFormatHint", "Formato JPG, PNG o GIF. Tamaño máximo 5 MB.")}
          </p>
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="h-20 w-20 rounded-lg border-2 border-slate-200 object-contain bg-white p-1"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
                <Building2 className="h-8 w-8 text-slate-400" />
              </div>
            )}
            <div className="flex-1">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <Camera className="h-4 w-4" />
                {uploadingLogo ? "Subiendo..." : "Subir logo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingLogo}
                  onChange={(e) =>
                    handleImageUpload(e.target.files?.[0], "logo", setUploadingLogo)
                  }
                />
              </label>
              <input type="hidden" {...register("logo")} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacto */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-5 w-5 text-slate-500" />
            {t("app.profile.contact", "Contacto")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.phone", "Teléfono")}</Label>
            <Input
              {...register("phone", { required: true })}
              className={inputClass}
              placeholder={t("app.profile.phPhone", "Ej: +54 11 1234-5678")}
            />
            {errors.phone && (
              <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
            )}
            <p className="mt-1.5 text-xs text-slate-500">
              {t("app.profile.phoneInstitutionHint", "Este número se muestra en la página pública de la institución.")}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Dirección */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-5 w-5 text-slate-500" />
            {t("app.profile.address", "Dirección")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.street", "Calle y número")}</Label>
            <Input
              {...register("address_street")}
              className={inputClass}
              placeholder={t("app.profile.phStreet", "Ej: Av. Corrientes 1234")}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.floor", "Piso")}</Label>
              <Input
                {...register("address_floor")}
                className={inputClass}
                placeholder={t("app.profile.phFloor", "Ej: 2")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.apt", "Depto")}</Label>
              <Input
                {...register("address_apt")}
                className={inputClass}
                placeholder={t("app.profile.phApt", "Ej: A")}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className={labelClass}>{t("app.profile.city", "Localidad")}</Label>
              <Input
                {...register("city")}
                className={inputClass}
                placeholder={t("app.profile.phCity", "Ej: CABA")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.province", "Provincia")}</Label>
              <Input
                {...register("province")}
                className={inputClass}
                placeholder={t("app.profile.phProvince", "Ej: Buenos Aires")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.postalCode", "Código Postal")}</Label>
              <Input
                {...register("postal_code")}
                className={inputClass}
                placeholder={t("app.profile.phPostal", "Ej: 1043")}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fechas (solo lectura) */}
      <Card className="overflow-hidden border-slate-200 bg-slate-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-slate-600">
            <Clock className="h-5 w-5 text-slate-500" />
            {t("app.profile.registration", "Registro")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-0 text-sm text-slate-600">
          {user.created_at && (
            <p>
              <span className="font-medium">{t("app.profile.createdAt", "Fecha de creación:")}</span>{" "}
              {formatDateDMY(user.created_at)}
            </p>
          )}
          {user.updated_at && (
            <p>
              <span className="font-medium">{t("app.profile.updatedAt", "Última modificación:")}</span>{" "}
              {formatDateDMY(user.updated_at)}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AssociationProfileFields({
  register,
  errors,
  user,
  watch,
  setValue,
}) {
  const { t } = useBrandTranslation();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoUrl = watch("logo");

  const inputClass =
    "w-full rounded border border-slate-200 p-2.5 text-slate-700 text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  const handleImageUpload = async (file, field, setUploading) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      customToast(t("app.profile.fileTooLarge", "El archivo no puede superar los 5 MB"), "danger");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("key", config.imgbbApiKey);
      const response = await fetch("https://api.imgbb.com/1/upload", {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        setValue(field, result.data.url);
      }
    } catch (err) {
      console.error("Error uploading image:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 border-t border-gray-200 pt-6">
      {/* Datos de la asociación */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-5 w-5 text-purple-500" />
            Datos de la asociación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.fantasyName", "Nombre Fantasía")}</Label>
            <Input
              {...register("fantasy_name")}
              className={inputClass}
              placeholder={t("app.profile.phAssociation", "Ej: Asociación Médica Argentina")}
            />
          </div>
        </CardContent>
      </Card>

      {/* Logo */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-5 w-5 text-slate-500" />
            Logo de la asociación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <p className="text-sm text-slate-500">
            {t("app.profile.logoFormatHint", "Formato JPG, PNG o GIF. Tamaño máximo 5 MB.")}
          </p>
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="h-20 w-20 rounded-lg border-2 border-slate-200 object-contain bg-white p-1"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
                <Landmark className="h-8 w-8 text-slate-400" />
              </div>
            )}
            <div className="flex-1">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <Camera className="h-4 w-4" />
                {uploadingLogo ? "Subiendo..." : "Subir logo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingLogo}
                  onChange={(e) =>
                    handleImageUpload(e.target.files?.[0], "logo", setUploadingLogo)
                  }
                />
              </label>
              <input type="hidden" {...register("logo")} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacto */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-5 w-5 text-slate-500" />
            {t("app.profile.contact", "Contacto")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.phone", "Teléfono")}</Label>
            <Input
              {...register("phone", { required: true })}
              className={inputClass}
              placeholder={t("app.profile.phPhone", "Ej: +54 11 1234-5678")}
            />
            {errors.phone && (
              <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
            )}
            <p className="mt-1.5 text-xs text-slate-500">
              {t("app.profile.phoneAssociationHint", "Este número se muestra en la página pública de la asociación. El email se edita arriba junto con nombre y apellido.")}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Dirección */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-5 w-5 text-slate-500" />
            {t("app.profile.address", "Dirección")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className={labelClass}>{t("app.profile.street", "Calle y número")}</Label>
            <Input
              {...register("address_street")}
              className={inputClass}
              placeholder={t("app.profile.phStreet", "Ej: Av. Corrientes 1234")}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={labelClass}>{t("app.profile.floor", "Piso")}</Label>
              <Input
                {...register("address_floor")}
                className={inputClass}
                placeholder={t("app.profile.phFloor", "Ej: 2")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.apt", "Depto")}</Label>
              <Input
                {...register("address_apt")}
                className={inputClass}
                placeholder={t("app.profile.phApt", "Ej: A")}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className={labelClass}>{t("app.profile.city", "Localidad")}</Label>
              <Input
                {...register("city")}
                className={inputClass}
                placeholder={t("app.profile.phCity", "Ej: CABA")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.province", "Provincia")}</Label>
              <Input
                {...register("province")}
                className={inputClass}
                placeholder={t("app.profile.phProvince", "Ej: Buenos Aires")}
              />
            </div>
            <div>
              <Label className={labelClass}>{t("app.profile.postalCode", "Código Postal")}</Label>
              <Input
                {...register("postal_code")}
                className={inputClass}
                placeholder={t("app.profile.phPostal", "Ej: 1043")}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fechas (solo lectura) */}
      <Card className="overflow-hidden border-slate-200 bg-slate-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-slate-600">
            <Clock className="h-5 w-5 text-slate-500" />
            {t("app.profile.registration", "Registro")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-0 text-sm text-slate-600">
          {user.created_at && (
            <p>
              <span className="font-medium">{t("app.profile.createdAt", "Fecha de creación:")}</span>{" "}
              {formatDateDMY(user.created_at)}
            </p>
          )}
          {user.updated_at && (
            <p>
              <span className="font-medium">{t("app.profile.updatedAt", "Última modificación:")}</span>{" "}
              {formatDateDMY(user.updated_at)}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function Profile() {
  const { t } = useBrandTranslation();
  const { user: userAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const userId = userAuth?.id;
  const [activeTab, setActiveTab] = useState("profile");

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryUsersKey(userId),
    queryFn: () => useUserByIdQuery(userId),
    enabled: !!userId, // Solo ejecutar si userId está disponible
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, body }) => useUpdateUserMutation(userId, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryUsersKey() });
      console.log("Usuario actualizado:", data);
    },
    onError: (error) => {
      console.error("Error actualizando usuario:", error);
    },
  });

  useEffect(() => {
    if (user) {
      reset(user);
    }
  }, [user, reset]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "subscription" && user?.role === "doctor") {
      setActiveTab("subscription");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
    if (t === "team" && user?.role === "doctor") {
      setActiveTab("team");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
    if (t === "studies" && user?.role === "patient") {
      setActiveTab("studies");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
    if (t === "currencies" && (user?.role === "doctor" || user?.role === "institution")) {
      setActiveTab("currencies");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
    if (t === "patientData" && user?.role === "doctor") {
      setActiveTab("patientData");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
    if (t === "dependents" && canActAsPatient(user)) {
      setActiveTab("dependents");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
  }, [searchParams, setSearchParams, user, user?.role]);

  if (isLoading || !user) return <Spinner className="w-10 h-10 mx-auto" />;
  if (error) return <p className="text-red-500">{t("app.commonUi.error", "Error")}: {error.message}</p>;

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-2">
      <Button variant="outline" onClick={() => navigate("/")} className="mt-2 mb-3">
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t("common.backHome", "Volver al Inicio")}
      </Button>
      <div className="mb-3 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t("app.profile.settingsTitle", "Settings")}</h1>
        {/* <LanguageSwitcher variant="dark" /> */}
      </div>

      <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="w-full">
        <Tabs.List
          className={`grid gap-1 bg-gray-100 p-2 rounded-lg ${
            user?.role === "doctor"
              ? "grid-cols-2 sm:grid-cols-4 lg:grid-cols-8"
              : user?.role === "patient"
                ? "grid-cols-3"
                : user?.role === "institution"
                  ? "grid-cols-2"
                  : "grid-cols-1"
          }`}
        >
          <Tabs.Trigger
            value="profile"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
          >
            {t("app.profile.tabInfo", "Información")}
          </Tabs.Trigger>

          {user?.role === "patient" && (
            <Tabs.Trigger
              value="studies"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("app.profile.tabStudies", "Mis Estudios")}
            </Tabs.Trigger>
          )}

          {/* El paciente carga estos datos dentro de "Información"; el profesional,
              que puede atenderse como paciente, los tiene en su propia tab. */}
          {user?.role === "doctor" && (
            <Tabs.Trigger
              value="patientData"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("app.profile.tabPatientData", "Mi salud")}
            </Tabs.Trigger>
          )}

          {canActAsPatient(user) && (
            <Tabs.Trigger
              value="dependents"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("app.profile.tabDependents", "Familiares")}
            </Tabs.Trigger>
          )}

          {user?.role === "doctor" && (
            <Tabs.Trigger
              value="subscription"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("app.profile.tabSubscription", "Suscripción")}
            </Tabs.Trigger>
          )}

          {user?.role === "doctor" && (
            <Tabs.Trigger
              value="team"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("app.profile.tabTeam", "Equipo")}
            </Tabs.Trigger>
          )}
          {user?.role === "doctor" && (
            <Tabs.Trigger
              value="billing"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("app.profile.tabBilling", "Facturacion")}
            </Tabs.Trigger>
          )}
          {user?.role === "doctor" && (
            <Tabs.Trigger
              value="config"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("app.profile.tabConfig", "Configuración")}
            </Tabs.Trigger>
          )}
          {(user?.role === "doctor" || user?.role === "institution") && (
            <Tabs.Trigger
              value="currencies"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            >
              {t("inventory.currencies.title", "Monedas")}
            </Tabs.Trigger>
          )}
        </Tabs.List>

        <Tabs.Content value="profile" className="space-y-6 px-4 pt-4 mb-[100px]">
          <ProfileTab userId={userId} />
        </Tabs.Content>

        {(user?.role === "doctor" || user?.role === "institution") && (
          <Tabs.Content value="currencies" className="space-y-6 px-4 pt-4 mb-[100px]">
            <MonedasTab />
          </Tabs.Content>
        )}

        {user?.role === "patient" && (
          <Tabs.Content value="studies" className="space-y-6 px-4 pt-4 mb-[100px]">
            <PatientStudiesTab userId={userId} />
          </Tabs.Content>
        )}

        {user?.role === "doctor" && (
          <Tabs.Content value="patientData" className="space-y-6 px-4 pt-4 mb-[100px]">
            <PatientDataTab userId={userId} />
          </Tabs.Content>
        )}

        {canActAsPatient(user) && (
          <Tabs.Content value="dependents" className="space-y-6 px-4 pt-4 mb-[100px]">
            <DependentsTab />
          </Tabs.Content>
        )}

        {user?.role === "doctor" && (
          <Tabs.Content value="subscription" className="space-y-6 px-4 pt-4 mb-[100px]">
            <SubscriptionTab userId={userId} />
          </Tabs.Content>
        )}

        {user?.role === "doctor" && (
          <Tabs.Content value="team" className="space-y-6 px-4 pt-4 mb-[100px]">
            <SecondTab doctorId={userId} />
          </Tabs.Content>
        )}
        {user?.role === "doctor" && (
          <Tabs.Content value="billing" className="space-y-6 px-4 pt-4 mb-[100px]">
            <ThirdTab userId={userId} />
          </Tabs.Content>
        )}
        {user?.role === "doctor" && (
          <Tabs.Content value="config" className="space-y-6 px-4 pt-4 mb-[100px]">
            <DoctorConfigTab userId={userId} />
          </Tabs.Content>
        )}
        {/* {user?.role === "doctor" && (
          <Tabs.Content value="settings" className="space-y-6 px-4 pt-4 mb-[100px]">
            <FourthTab />
          </Tabs.Content>
        )} */}
      </Tabs.Root>
    </div>
  );
}

function ProfileTab({ userId }) {
  const { t } = useBrandTranslation();
  const queryClient = useQueryClient();
  const { loadData } = useData();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();
  const newPassword = watch("new_password");
  const confirmPassword = watch("confirm_password");

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryUsersKey(userId),
    queryFn: () => useUserByIdQuery(userId),
    enabled: !!userId, // Solo ejecutar si userId está disponible
  });

  // Obtener la asociación del profesional si es doctor
  const {
    data: associationData,
    isLoading: isLoadingAssociation,
  } = useQuery({
    queryKey: ["professional-association", userId],
    queryFn: async () => {
      if (!userId || user?.role !== "doctor") return null;
      const response = await fetch(
        `${config.baseUrlApi}/professionals/${userId}/association`
      );
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!userId && user?.role === "doctor",
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, body }) => useUpdateUserMutation(userId, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryUsersKey() });
      if (user?.role === "patient") {
        queryClient.invalidateQueries({
          queryKey: queryPatientHealthHistoryKey(user.id),
        });
      }
      loadData();
      reset(data);
      console.log("Usuario actualizado:", data);
    },
    onError: (error) => {
      console.error("Error actualizando usuario:", error);
    },
  });

  useEffect(() => {
    if (user) {
      reset(user);
    }
  }, [user, reset]);

  const onSubmit = async (data) => {
    const userId = user?.id;
    const { current_password, new_password, confirm_password, ...rest } = data;
    if (new_password || confirm_password) {
      if (new_password !== confirm_password) {
        return; // El mensaje "Las contraseñas no coinciden" ya se muestra en el formulario
      }
    }
    const body = { ...rest };
    if (new_password && confirm_password) {
      body.password = new_password;
    }
    await updateMutation.mutateAsync({ userId, body });
    if (body.password) {
      reset({ ...rest, current_password: "", new_password: "", confirm_password: "" });
    }
  };

  return (
    <div className="h-full">
      {isLoading && <Spinner className="w-10 h-10 mx-auto" />}
      {error && (
        <p className="text-red-500">
          {t("app.profile.loadError", "Hubo un error al cargar los datos del usuario:")} {error.message}
        </p>
      )}

      {user && (
        <>
          <h2 className="text-lg font-semibold mb-4">{t("app.profile.tabInfo", "Información")}</h2>

          {/* Mostrar logo de asociación si está suscrito */}
          {user.role === "doctor" && associationData && associationData.association && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-3">
                {associationData.association.picture ? (
                  <img
                    src={associationData.association.picture}
                    alt={associationData.association.hospital_name || "Asociación"}
                    className="w-16 h-16 object-cover rounded-lg border-2 border-purple-300"
                  />
                ) : (
                  <div className="w-16 h-16 bg-purple-200 rounded-lg flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-purple-600" />
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">{t("app.profile.associatedTo", "Asociado a:")}</p>
                  <p className="font-semibold text-purple-900">
                    {associationData.association.hospital_name || 
                     `${associationData.association.name} ${associationData.association.last_name}`}
                  </p>
                  <a
                    href={`/association/${associationData.association.id}`}
                    className="text-sm text-purple-600 hover:text-purple-800 underline"
                  >
                    Ver asociación
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Mostrar logo de asociación si está suscrito */}
          {user.role === "doctor" && associationData && associationData.association && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-3">
                {associationData.association.picture ? (
                  <img
                    src={associationData.association.picture}
                    alt={associationData.association.hospital_name || "Asociación"}
                    className="w-16 h-16 object-cover rounded-lg border-2 border-purple-300"
                  />
                ) : (
                  <div className="w-16 h-16 bg-purple-200 rounded-lg flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-purple-600" />
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">{t("app.profile.associatedTo", "Asociado a:")}</p>
                  <p className="font-semibold text-purple-900">
                    {associationData.association.hospital_name || 
                     `${associationData.association.name} ${associationData.association.last_name}`}
                  </p>
                  <a
                    href={`/association/${associationData.association.id}`}
                    className="text-sm text-purple-600 hover:text-purple-800 underline"
                  >
                    Ver asociación
                  </a>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mb-16">
            <div className="">
              {[
                { label: t("app.profile.name", "Nombre"), name: "name" },
                { label: t("app.profile.lastName", "Apellido"), name: "last_name" },
                { label: t("app.profile.email", "Email"), name: "email", type: "email" },
              ].map(({ label, name, type = "text" }) => (
                <div key={name} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <input
                    defaultValue={user[name]}
                    {...register(name, { required: true })}
                    type={type}
                    className="w-full rounded border border-slate-200 p-3 text-slate-700"
                    disabled={name === "email"}
                  />
                  {errors[name] && (
                    <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
                  )}
                </div>
              ))}
            </div>

            {user.role === "secretary" && (
              <Card className="overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Phone className="h-5 w-5 text-slate-500" />
                    {t("app.profile.contact", "Contacto")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <div>
                    <Label htmlFor="secretary-profile-phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </Label>
                    <Input
                      id="secretary-profile-phone"
                      {...register("phone", { required: true })}
                      className="w-full rounded border border-slate-200 p-3 text-slate-700"
                      placeholder={t("app.profile.phPhone", "Ej: +54 11 1234-5678")}
                    />
                    {errors.phone && (
                      <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Datos completos del paciente */}
            {user.role === "patient" && (
              <>
                <PatientProfileFields
                  register={register}
                  errors={errors}
                  user={user}
                  watch={watch}
                  setValue={setValue}
                  DISABILITY_OPTIONS={DISABILITY_OPTIONS(t)}
                  calcAge={calcAge}
                />
                <div className="flex justify-end">
                  <PatientHealthHistoryDialog
                    patientId={user.id}
                    patientName={`${user.name || ""} ${user.last_name || ""}`.trim()}
                    triggerVariant="outline"
                    triggerSize="default"
                  />
                </div>
              </>
            )}

            {/* Datos completos del profesional */}
            {user.role === "doctor" && (
              <DoctorProfileFields
                register={register}
                errors={errors}
                user={user}
                watch={watch}
                setValue={setValue}
                LANGUAGE_OPTIONS={LANGUAGE_OPTIONS}
                PROFESSIONAL_TITLES={PROFESSIONAL_TITLES(t)}
              />
            )}

            {/* Datos completos de la institución */}
            {user.role === "institution" && (
              <InstitutionProfileFields
                register={register}
                errors={errors}
                user={user}
                watch={watch}
                setValue={setValue}
                INSTITUTION_TYPES={INSTITUTION_TYPES(t)}
              />
            )}

            {/* Datos completos de la asociación */}
            {user.role === "association" && (
              <AssociationProfileFields
                register={register}
                errors={errors}
                user={user}
                watch={watch}
                setValue={setValue}
              />
            )}

            {/* Descripción (solo para asociaciones) */}
            {user?.role === "association" && (
              <div className="mb-6 border-t border-gray-200 pt-6">
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  Descripción pública
                </h3>
                <p className="text-sm text-gray-500 mb-2">
                  {t("app.profile.publicDescriptionHint", "Este texto se mostrará en la página pública de la asociación, debajo de la información de contacto.")}
                </p>
                <textarea
                  {...register("description")}
                  rows={4}
                  placeholder={t("app.profile.phDescription", "Ej: Somos una asociación de profesionales...")}
                  className="w-full rounded border border-slate-200 p-3 text-slate-700 resize-y min-h-[100px]"
                />
              </div>
            )}

            {/* Cambiar contraseña: contraseña actual (placeholder oculta), nueva y confirmar; icono ojo en cada uno */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">{t("app.profile.changePassword", "Cambiar contraseña")}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t("app.profile.passwordHint", "Dejar en blanco para no cambiar. La contraseña actual no se puede recuperar aquí por seguridad.")}
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("app.profile.currentPassword", "Contraseña actual")}
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    {...register("current_password")}
                    placeholder={t("app.profile.passwordPlaceholder", "••••••••")}
                    className="w-full rounded border border-slate-200 p-3 pr-10 text-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword((v) => !v)}
                    className="absolute right-3 text-gray-500 hover:text-gray-700"
                    aria-label={showCurrentPassword ? t("app.profile.hidePassword", "Ocultar contraseña") : t("app.profile.showPassword", "Ver contraseña")}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("app.profile.newPassword", "Nueva contraseña")}
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    {...register("new_password")}
                    placeholder={t("app.profile.passwordBlankPlaceholder", "Dejar en blanco para no cambiar")}
                    className="w-full rounded border border-slate-200 p-3 pr-10 text-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute right-3 text-gray-500 hover:text-gray-700"
                    aria-label={showNewPassword ? t("app.profile.hidePassword", "Ocultar contraseña") : t("app.profile.showPassword", "Ver contraseña")}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("app.profile.confirmPassword", "Confirmar nueva contraseña")}
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    {...register("confirm_password")}
                    placeholder={t("app.profile.passwordRepeatPlaceholder", "Repetir nueva contraseña")}
                    className="w-full rounded border border-slate-200 p-3 pr-10 text-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 text-gray-500 hover:text-gray-700"
                    aria-label={showConfirmPassword ? t("app.profile.hidePassword", "Ocultar contraseña") : t("app.profile.showPassword", "Ver contraseña")}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <span className="text-red-500 text-sm">{t("app.profile.passwordMismatch", "Las contraseñas no coinciden")}</span>
                )}
              </div>
            </div>

            {/* <div className="">
              <h2 className="text-lg font-semibold mb-2">{t("app.profile.preferences", "Preferencias")}</h2>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  {...register("notifications")}
                  className="mr-2"
                />
                <label className="text-gray-700">{t("app.profile.receiveNotifications", "Recibir notificaciones")}</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register("newsletter")}
                  className="mr-2"
                />
                <label className="text-gray-700">
                  Suscribirse al newsletter
                </label>
              </div>
            </div> */}

            <div className="flex gap-2 w-full justify-end items-center">
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  (Boolean(newPassword || confirmPassword) && newPassword !== confirmPassword)
                }
                className="w-full sm:w-[200px]"
              >
                {isSubmitting
                  ? t("app.profile.saving", "Guardando...")
                  : t("app.profile.saveChanges", "Guardar cambios")}
              </Button>
              <Button
                type="submit"
                onClick={() => (window.location.href = "/")}
                variant="secondary"
                className="w-full sm:w-[200px] border-gray-300 text-gray-700 hover:bg-gray-100 bg-gray-200"
              >
                {t("app.profile.goHome", "Ir al inicio")}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

// Datos del profesional en tanto paciente (se atiende con otro profesional).
// Son los mismos campos que carga un paciente en su tab "Información": datos
// personales, dirección, obra social, salud, discapacidad y contacto de emergencia.
// Ver CLAUDE.md → "Profesional como paciente".
function PatientDataTab({ userId }) {
  const { t } = useBrandTranslation();
  const queryClient = useQueryClient();
  const { loadData } = useData();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryUsersKey(userId),
    queryFn: () => useUserByIdQuery(userId),
    enabled: !!userId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ body }) => useUpdateUserMutation(userId, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryUsersKey() });
      queryClient.invalidateQueries({
        queryKey: queryPatientHealthHistoryKey(userId),
      });
      loadData();
      reset(data);
      customToast(t("app.profile.saved", "Datos guardados"), "success");
    },
    onError: (err) => {
      customToast(
        err.message || t("app.profile.saveError", "No se pudieron guardar los datos"),
        "danger"
      );
    },
  });

  useEffect(() => {
    if (user) reset(user);
  }, [user, reset]);

  const onSubmit = async (data) => {
    // Se envía el perfil completo (mismo patrón que la tab "Información").
    const { current_password, new_password, confirm_password, ...rest } = data;
    await updateMutation.mutateAsync({ body: rest });
  };

  return (
    <div className="h-full">
      {isLoading && <Spinner className="w-10 h-10 mx-auto" />}
      {error && (
        <p className="text-red-500">
          {t("app.profile.loadError", "Hubo un error al cargar los datos del usuario:")}{" "}
          {error.message}
        </p>
      )}

      {user && (
        <>
          <h2 className="text-lg font-semibold mb-1">
            {t("app.profile.patientDataTitle", "Mis datos de salud")}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t(
              "app.profile.patientDataIntro",
              "Estos datos son los que ve el profesional que te atiende cuando sacás un turno como paciente. No se muestran en tu perfil público."
            )}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mb-16">
            <PatientProfileFields
              register={register}
              errors={errors}
              user={user}
              watch={watch}
              setValue={setValue}
              DISABILITY_OPTIONS={DISABILITY_OPTIONS(t)}
              calcAge={calcAge}
              hideIdentityFields
            />

            <div className="flex justify-end">
              <PatientHealthHistoryDialog
                patientId={user.id}
                patientName={`${user.name || ""} ${user.last_name || ""}`.trim()}
                triggerVariant="outline"
                triggerSize="default"
              />
            </div>

            <div className="flex gap-2 w-full justify-end items-center">
              <Button type="submit" disabled={isSubmitting} className="w-full sm:w-[200px]">
                {isSubmitting
                  ? t("app.profile.saving", "Guardando...")
                  : t("app.profile.saveChanges", "Guardar cambios")}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

function SecondTab({ doctorId }) {
  const { t } = useBrandTranslation();
  const queryClient = useQueryClient();
  const [lastInvite, setLastInvite] = useState(null);
  const [serverError, setServerError] = useState("");
  const [feedback, setFeedback] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      last_name: "",
      email: "",
      phone: "",
    },
  });

  const {
    data,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: queryDoctorSecretariesKey(doctorId),
    queryFn: () => fetchDoctorSecretaries(doctorId),
    enabled: !!doctorId,
  });

  const addMutation = useMutation({
    mutationFn: (payload) => createDoctorSecretary(doctorId, payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: queryDoctorSecretariesKey(doctorId),
      });
      reset();
      setServerError("");
      setFeedback(t("app.profile.secretaryAdded", "Secretaria agregada correctamente."));

      if (response?.temporaryPassword) {
        const secretary = response?.relation?.secretary;
        setLastInvite({
          name: secretary
            ? `${secretary.name || ""} ${secretary.last_name || ""}`.trim()
            : "",
          email: secretary?.email || "",
          password: response.temporaryPassword,
        });
      } else {
        setLastInvite(null);
      }
    },
    onError: (err) => {
      setServerError(err.message || t("app.profile.secretaryAddError", "No se pudo agregar la secretaria."));
      setFeedback("");
      setLastInvite(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (secretaryId) =>
      removeDoctorSecretary(doctorId, secretaryId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryDoctorSecretariesKey(doctorId),
      });
      setServerError("");
      setFeedback(t("app.profile.secretaryRemoved", "Secretaria eliminada del equipo."));
    },
    onError: (err) => {
      setServerError(err.message || t("app.profile.secretaryRemoveError", "No se pudo eliminar la secretaria."));
      setFeedback("");
    },
  });

  const onSubmit = (formData) => {
    setServerError("");
    setFeedback("");
    addMutation.mutate(formData);
  };

  const secretaries = data?.data || [];
  const hasSecretaries = secretaries.length > 0;

  return (
    <div className="h-full space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">{t("app.profile.teamTitle", "Equipo")}</h2>
        <p className="text-gray-600 text-sm">
          {t("app.profile.teamIntro", "Invita secretarias para que administren tus turnos. Se generará una contraseña temporal que deberás compartirles. Cuando ingresen por primera vez se les solicitará cambiarla.")}
        </p>
      </div>

      <div className="border rounded-lg p-4 bg-white shadow-sm">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("app.profile.name", "Nombre")}
              </label>
              <input
                type="text"
                {...register("name", { required: true })}
                className="w-full rounded border border-slate-200 p-3 text-slate-700"
              />
              {errors.name && (
                <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("app.profile.lastName", "Apellido")}
              </label>
              <input
                type="text"
                {...register("last_name", { required: true })}
                className="w-full rounded border border-slate-200 p-3 text-slate-700"
              />
              {errors.last_name && (
                <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("app.profile.email", "Email")}
              </label>
              <input
                type="email"
                {...register("email", { required: true })}
                className="w-full rounded border border-slate-200 p-3 text-slate-700"
              />
              {errors.email && (
                <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("app.profile.phoneOptional", "Teléfono (opcional)")}
              </label>
              <input
                type="text"
                {...register("phone")}
                className="w-full rounded border border-slate-200 p-3 text-slate-700"
              />
            </div>
          </div>

          {serverError && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {serverError}
            </div>
          )}

          {feedback && (
            <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              {feedback}
            </div>
          )}

          {lastInvite && (
            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
              <p className="font-semibold">
                {t("app.profile.tempPasswordFor", "Contraseña temporal generada para {{name}}", { name: lastInvite.name || t("app.profile.tempPasswordNew", "la nueva secretaria") })}
              </p>
              <p className="mt-1">
                <span className="font-medium">{t("app.profile.email", "Email")}:</span> {lastInvite.email}
              </p>
              <p className="mt-1">
                <span className="font-medium">{t("app.profile.passwordLabel", "Contraseña:")}</span>{" "}
                <span className="font-mono">{lastInvite.password}</span>
              </p>
              <p className="mt-2 text-xs text-blue-800">
                {t("app.profile.tempPasswordShare", "Compartí esta contraseña manualmente. Al iniciar sesión deberán cambiarla para continuar.")}
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={addMutation.isPending}
              className="w-full sm:w-[220px]"
            >
              {addMutation.isPending
                ? t("app.profile.adding", "Agregando...")
                : t("app.profile.addSecretary", "Agregar secretaria")}
            </Button>
          </div>
        </form>
      </div>

      <div className="border rounded-lg p-4 bg-white shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {t("app.profile.assignedSecretaries", "Secretarias asignadas")}
          </h3>
          {isFetching && <Spinner className="w-5 h-5" />}
        </div>

        {isLoading ? (
          <Spinner className="w-10 h-10 mx-auto" />
        ) : error ? (
          <p className="text-red-500 text-sm">
            {t("app.profile.loadSecretariesError", "Error al cargar las secretarias:")} {error.message}
          </p>
        ) : hasSecretaries ? (
          <ul className="space-y-3">
            {secretaries.map((item) => {
              const secretary = item.secretary;
              const fullName = secretary
                ? `${secretary.name || ""} ${secretary.last_name || ""}`.trim()
                : t("app.profile.secretaryDefault", "Secretaria");

              return (
                <li
                  key={item.id}
                  className="flex items-center justify-between rounded border border-gray-200 px-4 py-3"
                >
                  <div>
                    <p className="font-medium text-gray-900">{fullName}</p>
                    <p className="text-sm text-gray-600">{secretary?.email}</p>
                    {secretary?.phone && (
                      <p className="text-xs text-gray-500">
                        {t("app.profile.tel", "Tel:")} {secretary.phone}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removeMutation.mutate(item.secretary_id)}
                    disabled={removeMutation.isPending}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2Icon className="w-4 h-4" />
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-sm text-gray-600">
            {t("app.profile.noSecretaries", "Todavía no agregaste secretarias. Usá el formulario superior para invitarlas a tu equipo.")}
          </div>
        )}
      </div>
    </div>
  );
}

function ThirdTab({ userId }) {
  const { t } = useBrandTranslation();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryUsersKey(userId),
    queryFn: () => useUserByIdQuery(userId),
    enabled: !!userId, // Solo ejecutar si userId está disponible
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, body }) => useUpdateUserMutation(userId, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryUsersKey() });
      console.log("Usuario actualizado:", data);
    },
    onError: (error) => {
      console.error("Error actualizando usuario:", error);
    },
  });

  const onSubmit = async (data) => {
    const body = { ...data };
    const updatedUser = await updateMutation.mutateAsync({ userId, body });
  };

  return (
    <div className="h-full">
      <h2 className="text-lg font-semibold mb-4">{t("app.profile.tabBilling", "Facturacion")}</h2>
      <p>{t("app.profile.billingIntro", "Aquí puedes gestionar tu facturacion.")}</p>

      <div className="gap-2 flex mb-4 mt-2 text-sm text-blue-600">
        <a href="/invoices">{t("app.profile.viewInvoices", "Ver Facturas")}</a>
        <a href="/billing">{t("app.profile.configureAfip", "Configurar credenciales de Afip")}</a>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mb-16">
        <ul className="mt-4 space-y-2">
          <li className="flex items-center justify-between p-2 bg-gray-lighter rounded">
            <span>{t("app.profile.appointmentDuration", "Duración del turno")}</span>
            <input
              defaultValue={user?.appointment_duration}
              {...register("appointment_duration", { required: true })}
              type={"text"}
              className="rounded border border-slate-200 p-3 text-slate-700"
            />
            {errors.appointment_duration && (
              <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
            )}
          </li>
          <li className="flex items-center justify-between p-2 bg-gray-lighter rounded">
            <span>{t("app.profile.consultationCost", "Costo de la consulta")}</span>
            <input
              defaultValue={user?.private_appointment_price}
              {...register("private_appointment_price", { required: true })}
              type={"text"}
              className="rounded border border-slate-200 p-3 text-slate-700"
            />
            {errors.private_appointment_price && (
              <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
            )}
          </li>
          <li className="flex items-center justify-between p-2 bg-gray-lighter rounded">
            <div className="flex items-center">
              <span>{t("app.profile.mpToken", "Token Mercado Pago")}</span>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="ml-2 w-4 h-4 text-blue-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <TooltipArrow intent="dark" />
                  <p className="text-sm w-[300px]">
                    {t("app.profile.mpTokenTooltip", "El token de acceso es un string que identifica tu cuenta de Mercado Pago y permite autenticar las solicitudes a la API. Puedes encontrarlo en la configuración de tu cuenta de Mercado Pago, en la sección de credenciales.")}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <input
              defaultValue={user?.mp_user}
              {...register("mp_user", { required: true })}
              type={"text"}
              className="rounded border border-slate-200 p-3 text-slate-700"
            />
            {errors.mp_user && (
              <span className="text-red-500 text-sm">{t("app.profile.required", "* Obligatorio")}</span>
            )}
          </li>
        </ul>

        <div className="flex gap-2 w-full justify-end items-center">
          <Button
            type="submit"
            disabled={false}
            className="w-full sm:w-[200px]"
          >
            {t("app.profile.saveChanges", "Guardar cambios")}
          </Button>
          <Button
            type="submit"
            onClick={() => (window.location.href = "/")}
            variant="secondary"
            className="w-full sm:w-[200px] border-gray-300 text-gray-700 hover:bg-gray-100 bg-gray-200"
          >
            {t("app.profile.goHome", "Ir al inicio")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function SubscriptionTab({ userId }) {
  const { t } = useBrandTranslation();
  const { user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const price = config.subscription.price * config.subscription.conversionRate;
  const billingCycle = config.subscription.billingCycle; // Ciclo fijo mensual

  useEffect(() => {
    loadSubscription();
    refreshUser?.();
  }, [userId]);

  // Recargar suscripción cuando el componente se monte o cuando se regrese de MP
  useEffect(() => {
    const checkForRefresh = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("tab") === "subscription") {
        loadSubscription();
      }
    };
    checkForRefresh();
  }, []);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getUserSubscription(userId);
      setSubscription(data);
    } catch (err) {
      console.error("Error cargando suscripción:", err);
      setError(err.message || t("app.profile.subscriptionLoadError", "Error al cargar la suscripción"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    try {
      setIsCreating(true);
      setError("");
      const response = await createSubscription({
        userId,
        price: parseFloat(price),
        billingCycle: "monthly", // Siempre mensual
      });

      if (response.init_point) {
        // Redirigir a Mercado Pago para completar el pago
        window.location.href = response.init_point;
      } else {
        throw new Error(t("app.profile.paymentUrlError", "No se pudo obtener la URL de pago"));
      }
    } catch (err) {
      console.error("Error creando suscripción:", err);
      setError(err.message || t("app.profile.subscriptionCreateError", "Error al crear la suscripción"));
      setIsCreating(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      active: t("app.profile.statusActive", "Activa"),
      pending: t("app.profile.statusPending", "Pendiente"),
      paused: t("app.profile.statusPaused", "Pausada"),
      cancelled: t("app.profile.statusCancelled", "Cancelada"),
      inactive: t("app.profile.statusInactive", "Inactiva"),
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      active: "bg-green-100 text-green-800 border-green-300",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      paused: "bg-orange-100 text-orange-800 border-orange-300",
      cancelled: "bg-red-100 text-red-800 border-red-300",
      inactive: "bg-gray-100 text-gray-800 border-gray-300",
    };
    return colorMap[status] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  if (loading) {
    return <Spinner className="w-10 h-10 mx-auto" />;
  }

  const isServiceActive = user?.is_professional_enabled !== false;

  return (
    <div className="h-full space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">{t("app.profile.subscriptionTitle", "Suscripción")}</h2>
        <p className="text-gray-600 text-sm">
          {t("app.profile.subscriptionIntro", "Gestiona tu suscripción recurrente. Mantén tu cuenta activa con pagos automáticos mensuales.")}
        </p>
      </div>

      <div
        className={`rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${
          isServiceActive
            ? "bg-green-50 border-green-200"
            : "bg-red-50 border-red-200"
        }`}
      >
        <div>
          <p className="text-sm font-medium text-gray-700">
            {t("app.profile.serviceStatusLabel", "Estado del servicio")}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {isServiceActive
              ? t(
                  "app.profile.serviceActiveHint",
                  "Tu cuenta está habilitada en la plataforma. Podés recibir turnos y usar todas las funciones."
                )
              : t(
                  "app.profile.serviceInactiveHint",
                  "Tu cuenta aún no está habilitada. Una vez confirmado el pago, el administrador activará tu servicio."
                )}
          </p>
        </div>
        <span
          className={`text-lg font-semibold whitespace-nowrap ${
            isServiceActive ? "text-green-800" : "text-red-700"
          }`}
        >
          {isServiceActive
            ? t("app.profile.serviceActive", "Activa 💚")
            : t("app.profile.serviceInactive", "Inactiva 💔")}
        </span>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {subscription ? (
        <div className="border rounded-lg p-6 bg-white shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              Estado de la Suscripción
            </h3>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                subscription.status
              )}`}
            >
              {getStatusLabel(subscription.status)}
            </span>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">{t("app.profile.price", "Precio:")}</span>
              <span className="font-medium">
                ${subscription.price} {subscription.currency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t("app.profile.billingCycle", "Ciclo de facturación:")}</span>
              <span className="font-medium">
                {subscription.billing_cycle === "monthly"
                  ? "Mensual"
                  : "Anual"}
              </span>
            </div>
            {subscription.next_billing_date && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t("app.profile.nextBilling", "Próximo cobro:")}</span>
                <span className="font-medium">
                  {formatDateDMY(subscription.next_billing_date)}
                </span>
              </div>
            )}
            {subscription.last_payment_date && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t("app.profile.lastPayment", "Último pago:")}</span>
                <span className="font-medium">
                  {formatDateDMY(subscription.last_payment_date)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">{t("app.profile.createdAt", "Fecha de creación:")}</span>
              <span className="font-medium">
                {formatDateDMY(subscription.created_at)}
              </span>
            </div>
          </div>

          {subscription.status === "pending" && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800">
                {t("app.profile.pendingHint", "Tu suscripción está pendiente de aprobación. Completa el pago en Mercado Pago para activarla.")}
              </p>
            </div>
          )}

          {subscription.status === "active" && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800">
                {t("app.profile.activeHint", "✓ Tu suscripción está activa. Los pagos se realizarán automáticamente según tu ciclo de facturación.")}
              </p>
            </div>
          )}

          {(subscription.status === "cancelled" ||
            subscription.status === "inactive") && (
            <div className="mt-4">
              <Button
                onClick={handleCreateSubscription}
                disabled={isCreating}
                className="w-full"
              >
                {isCreating ? "Procesando..." : "Renovar Suscripción"}
              </Button>
            </div>
          )}
        </div>
      ) : (
        // <div className="border rounded-lg p-6 bg-white shadow-sm">
        //   <h3 className="text-base font-semibold text-gray-900 mb-4">
        //     Crear Suscripción
        //   </h3>
        //   <p className="text-gray-600 text-sm mb-6">
        //     No tienes una suscripción activa. Crea una suscripción recurrente
        //     para mantener tu cuenta activa.
        //   </p>

        //   <div className="space-y-4">
        //     <div>
        //       <label className="block text-sm font-medium text-gray-700 mb-1">
        //         Precio
        //       </label>
        //       <div className="w-full rounded border border-slate-200 p-3 bg-gray-50 text-slate-700">
        //         <span className="font-semibold">$9.90 USD</span>
        //         <p className="text-xs text-gray-500 mt-1">
        //           El cobro se realiza en pesos argentinos según la cotización del día
        //         </p>
        //       </div>
        //     </div>

        //     <div>
        //       <label className="block text-sm font-medium text-gray-700 mb-1">
        //         Ciclo de facturación
        //       </label>
        //       <div className="w-full rounded border border-slate-200 p-3 bg-gray-50 text-slate-700">
        //         Mensual
        //       </div>
        //     </div>

        //     <Button
        //       onClick={handleCreateSubscription}
        //       disabled={isCreating}
        //       className="w-full"
        //     >
        //       {isCreating ? "Procesando..." : "Crear Suscripción"}
        //     </Button>
        //   </div>
        // </div>
        <div className="border rounded-lg p-6 bg-white shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Crear Suscripción
          </h3>
          <p className="text-gray-600 text-sm mb-6">
            {t("app.profile.noActiveSubscription", "No tienes una suscripción activa. Crea una suscripción recurrente para mantener tu cuenta activa.")}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Precio
              </label>
              <div className="w-full rounded border border-slate-200 p-3 bg-gray-50 text-slate-700">
                <span className="font-semibold">$14.850</span>
                <p className="text-xs text-gray-500 mt-1">
                  {t("app.profile.debitedMonthly", "Se debitará de forma mensual")}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ciclo de facturación
              </label>
              <div className="w-full rounded border border-slate-200 p-3 bg-gray-50 text-slate-700">
                Mensual
              </div>
            </div>

            <Button
              onClick={() => {
                window.location.href = "https://www.mercadopago.com.ar/subscriptions/checkout?preapproval_plan_id=077e06802fdd43dab002a9cfb1998016";
              }}
              className="w-full"
            >
              Suscribirse
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// function FourthTab() {
//   return (
//     <div className="h-full">
//       <h2 className="text-lg font-semibold mb-4">Configuración</h2>
//       <p>Aquí puedes ajustar la configuración de tu cuenta.</p>
//       {/* Aquí puedes agregar más contenido relacionado con la configuración */}
//     </div>
//   );
// }

function DoctorConfigTab({ userId }) {
  const { t } = useBrandTranslation();
  const queryClient = useQueryClient();
  const { loadData } = useData();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm();

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryUsersKey(userId),
    queryFn: () => useUserByIdQuery(userId),
    enabled: !!userId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, body }) => useUpdateUserMutation(userId, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryUsersKey() });
      loadData();
      reset({
        patient_booking_horizon_days: normalizePatientBookingHorizonDays(
          data?.patient_booking_horizon_days
        ),
      });
      customToast(
        t("app.profile.configSaved", "Configuración guardada"),
        "success"
      );
    },
    onError: (err) => {
      customToast(
        err.message ||
          t("app.profile.configSaveError", "Error al guardar la configuración"),
        "danger"
      );
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        patient_booking_horizon_days: normalizePatientBookingHorizonDays(
          user.patient_booking_horizon_days
        ),
      });
    }
  }, [user, reset]);

  const onSubmit = async (data) => {
    await updateMutation.mutateAsync({
      userId,
      body: {
        patient_booking_horizon_days: normalizePatientBookingHorizonDays(
          data.patient_booking_horizon_days
        ),
      },
    });
  };

  if (isLoading) return <Spinner className="w-10 h-10 mx-auto" />;
  if (error) {
    return (
      <p className="text-red-500">
        {t("app.profile.loadError", "Hubo un error al cargar los datos del usuario:")}{" "}
        {error.message}
      </p>
    );
  }

  return (
    <div className="h-full max-w-xl">
      <h2 className="text-lg font-semibold mb-2">
        {t("app.profile.tabConfig", "Configuración")}
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        {t(
          "app.profile.bookingHorizonIntro",
          "Definí con cuánta anticipación tus pacientes pueden reservar turnos en la agenda."
        )}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t("app.profile.bookingHorizonTitle", "Visualización de turnos")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Label htmlFor="patient_booking_horizon_days">
              {t(
                "app.profile.bookingHorizonLabel",
                "Anticipación máxima para reservar (pacientes)"
              )}
            </Label>
            <select
              id="patient_booking_horizon_days"
              {...register("patient_booking_horizon_days", { required: true })}
              className="w-full rounded-md border border-slate-200 p-3 text-slate-700 bg-white"
            >
              {ALLOWED_PATIENT_BOOKING_HORIZON_DAYS.map((days) => (
                <option key={days} value={days}>
                  {t("app.profile.bookingHorizonOption", "{{days}} días", { days })}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              {t(
                "app.profile.bookingHorizonHint",
                "Los pacientes solo verán fechas disponibles dentro de este rango al reservar un turno."
              )}
            </p>
          </CardContent>
        </Card>

        <Button type="submit" disabled={isSubmitting || updateMutation.isPending}>
          {isSubmitting || updateMutation.isPending
            ? t("common.loading", "Cargando...")
            : t("common.save", "Guardar")}
        </Button>
      </form>
    </div>
  );
}
